import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme, TOKENS } from '../context/ThemeContext';
import axios from 'axios';
import { supabase } from '../supabase';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const API = 'https://trailcode-backend.onrender.com';
const Admin = () => {
  const { theme } = useTheme();
  const colors = TOKENS[theme];
  const navigate = useNavigate();

  const [challenges, setChallenges] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [form, setForm] = useState({
    title: '', category: 'apex', difficulty: 'Easy', topic: 'Apex Fundamentals',
    concept: '', doc_link: '', description: '', requirements: '',
    starter_code: '', test_class_body: '', class_name: '', test_class_name: '',
    lwc_component_name: '', lwc_starter_js: '', lwc_starter_html: '',
    jest_test_body: '', documentation_link: '', order_index: 0,
  });
  const [showForm, setShowForm] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Bulk import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [importStatus, setImportStatus] = useState(null); // { done, total, errors }
  const [importing, setImporting] = useState(false);
  const [activeTab, setActiveTab] = useState('challenges');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);

  const labelStyle = { display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: colors.text };
  const inputStyle = { width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: '6px', fontSize: '13px', fontFamily: 'monospace', backgroundColor: colors.bg, color: colors.text, boxSizing: 'border-box' };
  const buttonStyle = { padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' };

  useEffect(() => {
    fetchChallenges();
  }, []);

  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      // 1. Total users + subscription breakdown
      const { data: users, error: usersErr } = await supabase
        .from('users')
        .select('id, subscription, created_at');

      // 2. Total submissions
      const { data: submissions, error: subsErr } = await supabase
        .from('submissions')
        .select('id, challenge_id, success, submitted_at');

      // 3. Challenges for name lookup
      const { data: challenges, error: chErr } = await supabase
        .from('challenges')
        .select('id, title, category, difficulty');

      console.log('Analytics fetch:', { users, submissions, challenges, usersErr, subsErr, chErr });

      if (usersErr || subsErr || chErr) {
        setAnalyticsError(`DB error: ${(usersErr || subsErr || chErr).message}`);
        return;
      }
      if (!users || !submissions || !challenges) {
        setAnalyticsError('No data returned — check Supabase RLS policies for users and submissions tables.');
        return;
      }

      // --- User metrics ---
      const totalUsers = users.length;
      const proUsers = users.filter(u => u.subscription === 'pro' || u.subscription === 'team').length;
      const freeUsers = totalUsers - proUsers;

      // Signups per day last 30 days
      const signupMap = {};
      users.filter(u => u.created_at && new Date(u.created_at) >= thirtyDaysAgo).forEach(u => {
        const day = u.created_at.slice(0, 10);
        signupMap[day] = (signupMap[day] || 0) + 1;
      });

      // Active users this week (submitted in last 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const activeThisWeek = submissions.filter(s => s.submitted_at && new Date(s.submitted_at) >= sevenDaysAgo)
        .reduce((acc, s) => { acc.add(s.challenge_id); return acc; }, new Set()).size;

      // --- Submission metrics ---
      const totalSubmissions = submissions.length;
      const successfulSubmissions = submissions.filter(s => s.success).length;
      const overallSuccessRate = totalSubmissions > 0 ? Math.round((successfulSubmissions / totalSubmissions) * 100) : 0;

      // Submissions per day last 30 days
      const subMap = {};
      submissions.filter(s => s.submitted_at && new Date(s.submitted_at) >= thirtyDaysAgo).forEach(s => {
        const day = s.submitted_at.slice(0, 10);
        subMap[day] = (subMap[day] || 0) + 1;
      });

      // Build 30-day arrays
      const days = [];
      for (let i = 29; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const key = d.toISOString().slice(0, 10);
        const label = d.toLocaleString('default', { month: 'short', day: 'numeric' });
        days.push({ date: key, label, signups: signupMap[key] || 0, submissions: subMap[key] || 0 });
      }

      // --- Challenge metrics ---
      const challengeMap = {};
      challenges.forEach(c => { challengeMap[c.id] = c; });

      const attemptMap = {};
      const successMap = {};
      submissions.forEach(s => {
        if (!challengeMap[s.challenge_id]) return;
        attemptMap[s.challenge_id] = (attemptMap[s.challenge_id] || 0) + 1;
        if (s.success) successMap[s.challenge_id] = (successMap[s.challenge_id] || 0) + 1;
      });

      const topAttempted = Object.entries(attemptMap)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([id, count]) => ({
          name: challengeMap[id]?.title?.replace(/^Day \d+ — /, '') || 'Unknown',
          attempts: count,
          category: challengeMap[id]?.category || '',
        }));

      const hardest = Object.entries(attemptMap)
        .filter(([id, count]) => count >= 5)
        .map(([id, attempts]) => {
          const solved = successMap[id] || 0;
          const failRate = Math.round(((attempts - solved) / attempts) * 100);
          return {
            name: challengeMap[id]?.title?.replace(/^Day \d+ — /, '') || 'Unknown',
            failRate,
            attempts,
            difficulty: challengeMap[id]?.difficulty || '',
          };
        })
        .sort((a, b) => b.failRate - a.failRate)
        .slice(0, 10);

      setAnalytics({
        totalUsers, proUsers, freeUsers,
        totalSubmissions, successfulSubmissions, overallSuccessRate,
        activeThisWeek,
        days,
        topAttempted,
        hardest,
      });
    } catch (e) {
      console.error('Analytics error:', e);
      setAnalyticsError(e.message || 'Unknown error');
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'analytics' && !analytics) fetchAnalytics();
  }, [activeTab, analytics, fetchAnalytics]);

  const fetchChallenges = async () => {
    try {
      const [regular, apex30] = await Promise.all([
        axios.get(`${API}/challenges`),
        axios.get(`${API}/challenges?category=apex30`),
      ]);
      const all = [...regular.data, ...apex30.data];
      all.sort((a, b) => {
        if (a.category === b.category) return (a.order_index || 0) - (b.order_index || 0);
        return a.category.localeCompare(b.category);
      });
      setChallenges(all);
    } catch (e) {
      console.error('Fetch error:', e);
    }
  };

  const updateForm = (updates) => {
    setForm(prev => ({ ...prev, ...updates }));
  };

  const handleEdit = async (challenge) => {
    try {
      const res = await axios.get(`${API}/challenges/${challenge.id}`);
      const fullChallenge = res.data;
      const mapped = {
        title: fullChallenge.title || '',
        category: fullChallenge.category || 'apex',
        difficulty: fullChallenge.difficulty || 'Easy',
        topic: fullChallenge.topic || 'Apex Fundamentals',
        description: fullChallenge.description || '',
        concept: fullChallenge.concept || '',
        doc_link: fullChallenge.doc_link || '',
        documentation_link: fullChallenge.documentation_link || '',
        order_index: fullChallenge.order_index || 0,
        class_name: fullChallenge.class_name || '',
        test_class_name: fullChallenge.test_class_name || '',
        starter_code: fullChallenge.starter_code || '',
        test_class_body: fullChallenge.test_class_body || '',
        lwc_component_name: fullChallenge.lwc_component_name || '',
        lwc_starter_js: fullChallenge.lwc_starter_js || '',
        lwc_starter_html: fullChallenge.lwc_starter_html || '',
        jest_test_body: fullChallenge.jest_test_body || '',
        requirements: Array.isArray(fullChallenge.requirements)
          ? fullChallenge.requirements.join('\n')
          : (fullChallenge.requirements || ''),
      };
      setForm(mapped);
      setEditingChallenge(fullChallenge);
      setShowForm(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setMessage('❌ Failed to load challenge');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      title: form.title, category: form.category, difficulty: form.difficulty,
      topic: form.topic, description: form.description, concept: form.concept,
      doc_link: form.doc_link, documentation_link: form.documentation_link,
      order_index: form.order_index || 0, class_name: form.class_name,
      test_class_name: form.test_class_name, starter_code: form.starter_code,
      test_class_body: form.test_class_body, lwc_component_name: form.lwc_component_name,
      lwc_starter_js: form.lwc_starter_js, lwc_starter_html: form.lwc_starter_html,
      jest_test_body: form.jest_test_body,
      requirements: form.requirements.split('\n').filter(r => r.trim()),
      is_active: true,
    };
    try {
      if (editingChallenge) {
        await axios.patch(`${API}/challenges/${editingChallenge.id}`, payload);
        setMessage('✅ Challenge updated');
      } else {
        await axios.post(`${API}/challenges`, payload);
        setMessage('✅ Challenge created');
      }
      setForm({
        title: '', category: 'apex', difficulty: 'Easy', topic: 'Apex Fundamentals',
        concept: '', doc_link: '', description: '', requirements: '',
        starter_code: '', test_class_body: '', class_name: '', test_class_name: '',
        lwc_component_name: '', lwc_starter_js: '', lwc_starter_html: '',
        jest_test_body: '', documentation_link: '', order_index: 0,
      });
      setEditingChallenge(null);
      setShowForm(false);
      fetchChallenges();
    } catch (e) {
      setMessage('❌ ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this challenge?')) {
      try {
        await axios.delete(`${API}/challenges/${id}`);
        setMessage('✅ Deleted');
        fetchChallenges();
      } catch (e) {
        setMessage('❌ Delete failed');
      }
    }
  };

  // ── Bulk Import ────────────────────────────────────────────
  const handleBulkImport = async () => {
    setImportStatus(null);

    let parsed;
    try {
      parsed = JSON.parse(importJson);
      if (!Array.isArray(parsed)) parsed = [parsed];
    } catch (err) {
      setImportStatus({ done: 0, total: 0, errors: ['Invalid JSON — could not parse'] });
      return;
    }

    setImporting(true);
    const errors = [];
    let done = 0;

    for (let i = 0; i < parsed.length; i++) {
      const ch = parsed[i];
      const payload = {
        title: ch.title || '',
        category: ch.category || 'apex',
        difficulty: ch.difficulty || 'Easy',
        topic: ch.topic || '',
        description: ch.description || '',
        concept: ch.concept || '',
        doc_link: ch.doc_link || '',
        documentation_link: ch.documentation_link || '',
        order_index: ch.order_index || 0,
        class_name: ch.class_name || '',
        test_class_name: ch.test_class_name || '',
        starter_code: ch.starter_code || '',
        test_class_body: ch.test_class_body || '',
        lwc_component_name: ch.lwc_component_name || '',
        lwc_starter_js: ch.lwc_starter_js || '',
        lwc_starter_html: ch.lwc_starter_html || '',
        jest_test_body: ch.jest_test_body || '',
        requirements: Array.isArray(ch.requirements) ? ch.requirements : [],
        is_active: ch.is_active !== false,
        // LWC fields — must be included or dom checks / mock data are lost on import
        is_lwc: ch.is_lwc || false,
        is_lwc_integration: ch.is_lwc_integration || false,
        lwc_dom_checks: ch.lwc_dom_checks || [],
        lwc_mock_data: ch.lwc_mock_data || null,
      };

      try {
        await axios.post(`${API}/challenges`, payload);
        done++;
        // update progress live
        setImportStatus({ done, total: parsed.length, errors: [...errors] });
      } catch (e) {
        const msg = `[${i + 1}] "${ch.title}": ${e.response?.data?.error || e.message}`;
        errors.push(msg);
        setImportStatus({ done, total: parsed.length, errors: [...errors] });
      }
    }

    setImporting(false);
    fetchChallenges();
  };

  const grouped = {};
  challenges.forEach(c => {
    const t = c.topic || 'Uncategorized';
    if (!grouped[t]) grouped[t] = [];
    grouped[t].push(c);
  });

  // Category filter
  const allCategories = ['all', ...Array.from(new Set(challenges.map(c => c.category))).sort()];
  const categoryCounts = {};
  challenges.forEach(c => { categoryCounts[c.category] = (categoryCounts[c.category] || 0) + 1; });

  const filteredChallenges = categoryFilter === 'all' ? challenges : challenges.filter(c => c.category === categoryFilter);
  const filteredGrouped = {};
  filteredChallenges.forEach(c => {
    const t = c.topic || 'Uncategorized';
    if (!filteredGrouped[t]) filteredGrouped[t] = [];
    filteredGrouped[t].push(c);
  });

  // ── Render ─────────────────────────────────────────────────
  return (
    <div style={{ background: colors.bg, color: colors.text, minHeight: '100vh', padding: '24px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '8px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: '700' }}>Admin Panel</h1>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => { setShowImportModal(true); setImportJson(''); setImportStatus(null); }}
              style={{ ...buttonStyle, background: colors.success, color: '#fff' }}
            >
              📥 Bulk Import JSON
            </button>
            <button
              onClick={() => { setShowForm(!showForm); setEditingChallenge(null); }}
              style={{ ...buttonStyle, background: colors.accent, color: '#fff' }}
            >
              {showForm ? 'Cancel' : '+ New Challenge'}
            </button>
          </div>
        </div>

        {/* Tab switcher */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 4, width: 'fit-content' }}>
          {[{ key: 'challenges', label: '📋 Challenges' }, { key: 'analytics', label: '📊 Analytics' }].map(({ key, label }) => (
            <button key={key} onClick={() => setActiveTab(key)} style={{
              padding: '8px 20px', borderRadius: 7, border: activeTab === key ? `1px solid ${colors.border}` : '1px solid transparent',
              background: activeTab === key ? colors.bg : 'none', color: activeTab === key ? colors.text : colors.secondary,
              fontSize: 13, fontWeight: activeTab === key ? 700 : 500, cursor: 'pointer', transition: 'all 0.15s',
            }}>{label}</button>
          ))}
        </div>

        {/* Message */}
        {message && (
          <div style={{ padding: '12px 16px', background: colors.card, borderRadius: '6px', marginBottom: '16px', borderLeft: `4px solid ${colors.accent}` }}>
            {message}
          </div>
        )}

        {/* ── Bulk Import Modal ── */}
        {showImportModal && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}>
            <div style={{
              background: colors.card, border: `1px solid ${colors.border}`,
              borderRadius: '12px', padding: '28px', width: '680px', maxWidth: '95vw',
              maxHeight: '90vh', overflowY: 'auto',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '700' }}>Bulk Import Challenges</h2>
                <button
                  onClick={() => { if (!importing) setShowImportModal(false); }}
                  style={{ background: 'none', border: 'none', color: colors.secondary, fontSize: '20px', cursor: 'pointer' }}
                >✕</button>
              </div>

              <p style={{ fontSize: '13px', color: colors.secondary, marginBottom: '12px' }}>
                Paste a JSON array of challenge objects. Each will be submitted to the API individually.
              </p>

              <textarea
                value={importJson}
                onChange={e => setImportJson(e.target.value)}
                placeholder={'[\n  {\n    "title": "My Challenge",\n    "category": "apex",\n    ...\n  }\n]'}
                style={{
                  ...inputStyle,
                  minHeight: '240px', resize: 'vertical',
                  fontFamily: 'monospace', fontSize: '12px',
                  marginBottom: '16px',
                }}
                disabled={importing}
              />

              {/* Progress */}
              {importStatus && (
                <div style={{ marginBottom: '16px' }}>
                  {/* Progress bar */}
                  <div style={{ background: colors.bg, borderRadius: '4px', height: '8px', marginBottom: '8px', overflow: 'hidden' }}>
                    <div style={{
                      background: importStatus.errors.length > 0 ? colors.warning : colors.success,
                      height: '100%',
                      width: importStatus.total > 0 ? `${(importStatus.done / importStatus.total) * 100}%` : '0%',
                      transition: 'width 0.2s ease',
                      borderRadius: '4px',
                    }} />
                  </div>
                  <p style={{ fontSize: '13px', color: colors.text, marginBottom: '6px' }}>
                    {importing ? `Importing… ${importStatus.done} / ${importStatus.total}` : `Done — ${importStatus.done} inserted, ${importStatus.errors.length} failed`}
                  </p>
                  {importStatus.errors.length > 0 && (
                    <div style={{ background: colors.bg, borderRadius: '6px', padding: '10px', maxHeight: '120px', overflowY: 'auto' }}>
                      {importStatus.errors.map((err, i) => (
                        <p key={i} style={{ fontSize: '12px', color: colors.error, margin: '2px 0', fontFamily: 'monospace' }}>{err}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { if (!importing) setShowImportModal(false); }}
                  style={{ ...buttonStyle, background: colors.muted, color: colors.text }}
                  disabled={importing}
                >
                  {importStatus && !importing ? 'Close' : 'Cancel'}
                </button>
                <button
                  onClick={handleBulkImport}
                  disabled={importing || !importJson.trim()}
                  style={{
                    ...buttonStyle,
                    background: colors.success,
                    color: '#fff',
                    opacity: (importing || !importJson.trim()) ? 0.5 : 1,
                  }}
                >
                  {importing ? `⏳ Importing ${importStatus?.done || 0}/${importStatus?.total || '?'}…` : '🚀 Import All'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Single challenge form */}
        {showForm && (
          <div style={{ background: colors.card, padding: '20px', borderRadius: '8px', marginBottom: '24px' }}>
            <h2 style={{ marginBottom: '16px' }}>{editingChallenge ? 'Edit Challenge' : 'New Challenge'}</h2>

            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Category *</label>
              <select value={form.category} onChange={e => updateForm({ category: e.target.value })} style={inputStyle}>
                <option value="apex">Apex</option>
                <option value="integrations">Integrations</option>
                <option value="lwc">LWC</option>
                <option value="soql">SOQL</option>
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
              <div>
                <label style={labelStyle}>Title *</label>
                <input value={form.title} onChange={e => updateForm({ title: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Order Index</label>
                <input type="number" value={form.order_index} onChange={e => updateForm({ order_index: parseInt(e.target.value) || 0 })} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Difficulty</label>
                <select value={form.difficulty} onChange={e => updateForm({ difficulty: e.target.value })} style={inputStyle}>
                  <option>Easy</option>
                  <option>Medium</option>
                  <option>Hard</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Topic</label>
                <select value={form.topic} onChange={e => updateForm({ topic: e.target.value })} style={inputStyle}>
                  <option value="Apex Fundamentals">Apex Fundamentals</option>
                  <option value="Apex Testing">Apex Testing</option>
                  <option value="Asynchronous Apex">Asynchronous Apex</option>
                  <option value="Data Querying">Data Querying</option>
                  <option value="Data Manipulation">Data Manipulation</option>
                  <option value="Object-Oriented Apex">Object-Oriented Apex</option>
                  <option value="Apex Triggers">Apex Triggers</option>
                  <option value="Error Handling">Error Handling</option>
                  <option value="Custom Metadata">Custom Metadata</option>
                  <option value="Working with JSON">Working with JSON</option>
                  <option value="REST Callouts">REST Callouts</option>
                  <option value="Mocks &amp; Testing">Mocks &amp; Testing</option>
                  <option value="Platform Events">Platform Events</option>
                  <option value="LWC Fundamentals">LWC Fundamentals</option>
                  <option value="LWC Advanced">LWC Advanced</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Description *</label>
              <textarea value={form.description} onChange={e => updateForm({ description: e.target.value })} style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }} />
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Requirements (one per line)</label>
              <textarea value={form.requirements} onChange={e => updateForm({ requirements: e.target.value })} style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
              <div>
                <label style={labelStyle}>Concept (Learning Notes)</label>
                <textarea value={form.concept} onChange={e => updateForm({ concept: e.target.value })} style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} />
              </div>
              <div>
                <label style={labelStyle}>Doc Link</label>
                <input value={form.doc_link} onChange={e => updateForm({ doc_link: e.target.value })} style={inputStyle} />
              </div>
            </div>

            {form.category === 'apex' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                  <div>
                    <label style={labelStyle}>Class Name *</label>
                    <input value={form.class_name} onChange={e => updateForm({ class_name: e.target.value })} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Test Class Name *</label>
                    <input value={form.test_class_name} onChange={e => updateForm({ test_class_name: e.target.value })} style={inputStyle} />
                  </div>
                </div>
                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>Starter Code *</label>
                  <textarea value={form.starter_code} onChange={e => updateForm({ starter_code: e.target.value })} style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>Test Class Body *</label>
                  <textarea value={form.test_class_body} onChange={e => updateForm({ test_class_body: e.target.value })} style={{ ...inputStyle, minHeight: '150px', resize: 'vertical' }} />
                </div>
              </>
            )}

            {form.category === 'lwc' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                  <div>
                    <label style={labelStyle}>Component Name *</label>
                    <input value={form.lwc_component_name} onChange={e => updateForm({ lwc_component_name: e.target.value })} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Documentation Link</label>
                    <input value={form.documentation_link} onChange={e => updateForm({ documentation_link: e.target.value })} style={inputStyle} />
                  </div>
                </div>
                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>Starter JavaScript *</label>
                  <textarea value={form.lwc_starter_js} onChange={e => updateForm({ lwc_starter_js: e.target.value })} style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>Starter HTML *</label>
                  <textarea value={form.lwc_starter_html} onChange={e => updateForm({ lwc_starter_html: e.target.value })} style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>Jest Test Body *</label>
                  <textarea value={form.jest_test_body} onChange={e => updateForm({ jest_test_body: e.target.value })} style={{ ...inputStyle, minHeight: '150px', resize: 'vertical' }} />
                </div>
              </>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleSubmit} disabled={loading} style={{ ...buttonStyle, background: colors.accent, color: '#fff', opacity: loading ? 0.6 : 1 }}>
                {loading ? '⏳ Saving...' : editingChallenge ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'challenges' && <>

        {/* ── Category filter tabs ── */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: colors.secondary, marginRight: 4 }}>Filter:</span>
          {allCategories.map(cat => {
            const count = cat === 'all' ? challenges.length : (categoryCounts[cat] || 0);
            const isApex30 = cat === 'apex30';
            const active = categoryFilter === cat;
            return (
              <button key={cat} onClick={() => setCategoryFilter(cat)} style={{
                padding: '5px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                border: `1px solid ${active ? colors.accent : isApex30 ? 'rgba(79,142,247,0.4)' : colors.border}`,
                background: active ? colors.accent : isApex30 ? 'rgba(79,142,247,0.08)' : colors.card,
                color: active ? '#fff' : isApex30 ? colors.accent : colors.text,
              }}>
                {isApex30 ? '🔥 ' : ''}{cat} ({count})
              </button>
            );
          })}
        </div>

        {/* Challenges list */}
        <div>
          <h2 style={{ marginBottom: '16px' }}>
            {categoryFilter === 'all' ? `All Challenges (${challenges.length} total)` : `${categoryFilter} (${filteredChallenges.length})`}
          </h2>
          {Object.entries(filteredGrouped).map(([topic, topicChallenges]) => (
            <div key={topic} style={{ marginBottom: '32px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', paddingBottom: '10px', borderBottom: `1px solid ${colors.border}` }}>
                <h3 style={{ fontSize: '15px', fontWeight: '700', color: colors.accent }}>{topic}</h3>
                <span style={{ fontSize: '12px', color: colors.secondary }}>({topicChallenges.length})</span>
              </div>
              {topicChallenges.map(challenge => (
                <div key={challenge.id} style={{
                  background: colors.card,
                  border: `1px solid ${challenge.category === 'apex30' ? 'rgba(79,142,247,0.3)' : colors.border}`,
                  borderRadius: '8px', padding: '14px 18px', marginBottom: '8px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', color: colors.text, margin: 0 }}>
                        #{challenge.order_index || '?'} {challenge.title}
                      </h4>
                      {challenge.category === 'apex30' && (
                        <span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(79,142,247,0.15)', color: colors.accent, border: '1px solid rgba(79,142,247,0.3)', borderRadius: 4, padding: '1px 6px' }}>APEX30</span>
                      )}
                    </div>
                    <p style={{ fontSize: '12px', color: colors.secondary, marginBottom: '4px' }}>
                      {challenge.category} • {challenge.difficulty} • ID: {challenge.id}
                    </p>
                    <div style={{ fontSize: '11px', color: colors.muted }}>
                      {!challenge.concept && <span style={{ color: colors.error, marginRight: '8px' }}>⚠️ No concept</span>}
                      {!challenge.doc_link && <span style={{ color: colors.error, marginRight: '8px' }}>⚠️ No doc_link</span>}
                      {(challenge.category === 'apex' || challenge.category === 'apex30') && !challenge.class_name && <span style={{ color: colors.error, marginRight: '8px' }}>⚠️ No class_name</span>}
                      {(challenge.category === 'apex' || challenge.category === 'apex30') && !challenge.test_class_name && <span style={{ color: colors.error, marginRight: '8px' }}>⚠️ No test_class_name</span>}
                      {challenge.category === 'lwc' && !challenge.lwc_component_name && <span style={{ color: colors.error, marginRight: '8px' }}>⚠️ No lwc_component_name</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginLeft: 12 }}>
                    <button onClick={() => navigate(`/challenge/${challenge.id}`)} style={{ ...buttonStyle, background: colors.success, color: '#fff' }}>▶ Try</button>
                    <button onClick={() => handleEdit(challenge)} style={{ ...buttonStyle, background: colors.accent, color: '#fff' }}>✏️ Edit</button>
                    <button onClick={() => handleDelete(challenge.id)} style={{ ...buttonStyle, background: colors.error, color: '#fff' }}>🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        </>}

        {/* ── Analytics Tab ── */}
        {activeTab === 'analytics' && (
          <div>
            {analyticsLoading && <div style={{ textAlign: 'center', padding: 60, color: colors.secondary }}>Loading analytics…</div>}
            {!analyticsLoading && analyticsError && (
              <div style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid #f87171', borderRadius: 10, padding: '20px 24px', color: '#f87171', fontSize: 13 }}>
                <strong>Analytics failed to load:</strong> {analyticsError}
                <br/><br/>
                <span style={{ color: colors.secondary }}>Most likely cause: Supabase RLS is blocking the admin from reading all rows in the <code>users</code> or <code>submissions</code> table. Add a policy that allows your admin user to SELECT all rows, or temporarily disable RLS on those tables for testing.</span>
                <br/><br/>
                <button onClick={fetchAnalytics} style={{ ...buttonStyle, background: colors.card, color: colors.text, border: `1px solid ${colors.border}` }}>↻ Retry</button>
              </div>
            )}
            {!analyticsLoading && !analyticsError && analytics && (() => {
              const { totalUsers, proUsers, freeUsers, totalSubmissions, successfulSubmissions, overallSuccessRate, activeThisWeek, days, topAttempted, hardest } = analytics;
              const statCard = (label, value, sub, color) => (
                <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12, padding: '20px 24px', flex: 1, minWidth: 160 }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: color || colors.accent }}>{value}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: colors.text, marginTop: 4 }}>{label}</div>
                  {sub && <div style={{ fontSize: 11, color: colors.secondary, marginTop: 2 }}>{sub}</div>}
                </div>
              );
              const diffColor = d => d === 'Hard' ? '#f87171' : d === 'Medium' ? '#f59e0b' : '#10b981';
              return (
                <>
                  {/* Stat cards */}
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 28 }}>
                    {statCard('Total Users', totalUsers, `${proUsers} Pro · ${freeUsers} Free`, colors.accent)}
                    {statCard('Total Submissions', totalSubmissions, `${overallSuccessRate}% success rate`, '#10b981')}
                    {statCard('Successful Solves', successfulSubmissions, 'all time', '#22d3ee')}
                    {statCard('Active This Week', activeThisWeek, 'unique challenges attempted', '#f59e0b')}
                  </div>

                  {/* Charts row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
                    {[{ title: 'Signups (last 30 days)', key: 'signups', color: colors.accent },
                      { title: 'Submissions (last 30 days)', key: 'submissions', color: '#10b981' }].map(({ title, key, color }) => (
                      <div key={key} style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12, padding: '20px' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: colors.text, marginBottom: 16 }}>{title}</div>
                        <ResponsiveContainer width="100%" height={160}>
                          <BarChart data={days} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                            <XAxis dataKey="label" tick={{ fontSize: 9, fill: colors.secondary }} interval={6} />
                            <YAxis tick={{ fontSize: 9, fill: colors.secondary }} allowDecimals={false} />
                            <Tooltip contentStyle={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 12 }} />
                            <Bar dataKey={key} fill={color} radius={[3, 3, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ))}
                  </div>

                  {/* Top attempted + hardest */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
                    <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12, overflow: 'hidden' }}>
                      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${colors.border}`, fontWeight: 700, fontSize: 13, color: colors.text }}>🔥 Most Attempted</div>
                      {topAttempted.map((ch, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', borderBottom: `1px solid ${colors.border}` }}>
                          <span style={{ width: 20, fontSize: 12, fontWeight: 700, color: colors.secondary }}>{i + 1}</span>
                          <span style={{ flex: 1, fontSize: 12, color: colors.text }}>{ch.name}</span>
                          <span style={{ fontSize: 11, color: colors.accent, fontWeight: 700 }}>{ch.attempts} tries</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12, overflow: 'hidden' }}>
                      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${colors.border}`, fontWeight: 700, fontSize: 13, color: colors.text }}>💀 Hardest (≥5 attempts)</div>
                      {hardest.map((ch, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', borderBottom: `1px solid ${colors.border}` }}>
                          <span style={{ width: 20, fontSize: 12, fontWeight: 700, color: colors.secondary }}>{i + 1}</span>
                          <span style={{ flex: 1, fontSize: 12, color: colors.text }}>{ch.name}</span>
                          <span style={{ fontSize: 10, fontWeight: 700, color: diffColor(ch.difficulty), background: `${diffColor(ch.difficulty)}22`, borderRadius: 4, padding: '1px 6px' }}>{ch.difficulty}</span>
                          <span style={{ fontSize: 11, color: '#f87171', fontWeight: 700 }}>{ch.failRate}% fail</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Free vs Pro bar */}
                  <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12, padding: '20px 24px' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: colors.text, marginBottom: 12 }}>Free vs Pro</div>
                    <div style={{ display: 'flex', height: 24, borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
                      <div style={{ width: `${totalUsers > 0 ? (freeUsers / totalUsers) * 100 : 100}%`, background: colors.border, transition: 'width 0.4s' }} />
                      <div style={{ flex: 1, background: colors.accent }} />
                    </div>
                    <div style={{ display: 'flex', gap: 20, fontSize: 12, color: colors.secondary }}>
                      <span><span style={{ color: colors.text, fontWeight: 700 }}>{freeUsers}</span> Free ({totalUsers > 0 ? Math.round((freeUsers / totalUsers) * 100) : 0}%)</span>
                      <span><span style={{ color: colors.accent, fontWeight: 700 }}>{proUsers}</span> Pro ({totalUsers > 0 ? Math.round((proUsers / totalUsers) * 100) : 0}%)</span>
                    </div>
                  </div>

                  <div style={{ marginTop: 16, textAlign: 'right' }}>
                    <button onClick={() => { setAnalytics(null); fetchAnalytics(); }} style={{ ...buttonStyle, background: colors.card, color: colors.secondary, border: `1px solid ${colors.border}` }}>↻ Refresh</button>
                  </div>
                </>
              );
            })()}
          </div>
        )}

      </div>
    </div>
  );
};

export default Admin;